import { SelectProductsV2 } from "@components/auto-completes/products-v2";
import { SHStack } from "@components/design-systems";
import { LocalStorage } from "@constants";
import { useIsNew } from "@hooks/useIsNew";
import { useLocalStorage } from "@hooks/useLocalStorage";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import {
  AnalysisSetupDTO,
  AnalysisSetupStepRenderedFieldProps,
} from "@models/platform-analysis/entities/steps/setup";
import { useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useToggle } from "react-use";
import { ConfirmRefreshAnalysisDataDialog } from "../../../../../components/dialogs/confirm-refresh-platforms";

export const AnalysisSetupAdviserOtherPlatformSelection = ({
  disabled,
}: AnalysisSetupStepRenderedFieldProps) => {
  const isNew = useIsNew();
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AnalysisSetupDTO>();
  const {
    analysisPlatforms: storedAnalysisPlatforms,
    reachAnalysisSetupPage,
    setReachAnalysisSetupPage,
  } = usePlatformAnalysis();
  const { getLocalStorageItem, setLocalStorageItem } = useLocalStorage();
  const reachAnalysisSetupPageLocalStorage = getLocalStorageItem(
    LocalStorage.PlatformAnalysisReachAnalysisSetupPage,
  );

  const watchedOtherProductIds = watch("otherProductIds");
  const watchedMainProductId = watch("mainProductId");

  const shouldShowRefreshDialog = useMemo(
    () =>
      reachAnalysisSetupPageLocalStorage === "FIRST_TIME" ||
      reachAnalysisSetupPage === "FIRST_TIME",
    [reachAnalysisSetupPage, reachAnalysisSetupPageLocalStorage],
  );

  const dialogResultResolverRef = useRef<((result: boolean) => void) | null>(
    null,
  );
  const [refreshPlatformDialogOpen, toggleRefreshPlatformDialogOpen] =
    useToggle(false);

  const platformSelections = useMemo(
    () =>
      storedAnalysisPlatforms?.map((platform) => ({
        ...platform,
        isMain: platform.id === watchedMainProductId,
      })) ?? [],
    [storedAnalysisPlatforms, watchedMainProductId],
  );

  const selectedOtherPlatformIds: string[] | null = useMemo(() => {
    return (
      platformSelections
        ?.filter((platformSelection) => {
          return (
            watchedOtherProductIds?.includes(platformSelection.id) ||
            platformSelection.isMain
          );
        })
        ?.map((platform) => platform.id) || []
    );
  }, [platformSelections, watchedOtherProductIds]);

  // Functions
  const getRefreshPlatformsDialogResult = (): Promise<boolean> => {
    toggleRefreshPlatformDialogOpen(true);
    return new Promise((resolve) => {
      dialogResultResolverRef.current = (res: boolean) => {
        resolve(res);
        dialogResultResolverRef.current = null;
      };
    });
  };

  const onRefreshDialogSubmit = () => {
    toggleRefreshPlatformDialogOpen(false);
    dialogResultResolverRef.current?.(true);
  };

  const onRefreshDialogClose = () => {
    toggleRefreshPlatformDialogOpen(false);
    dialogResultResolverRef?.current?.(false);
  };

  const setReachSecondTime = () => {
    setReachAnalysisSetupPage("SECOND_TIME");
    setLocalStorageItem(
      LocalStorage.PlatformAnalysisReachAnalysisSetupPage,
      "SECOND_TIME",
    );
  };

  const getChangeValueResult = async (
    value: string[] | null,
  ): Promise<string[] | null> => {
    if (!shouldShowRefreshDialog) return value;
    setReachSecondTime();

    const dialogResult = await getRefreshPlatformsDialogResult();
    if (dialogResult) {
      handleOnRefreshPlatforms();
      return value;
    } else {
      return watchedOtherProductIds;
    }
  };

  const handleOnRefreshPlatforms = () => {
    setValue("hasRateCard", false);
    setValue("selectRateCards", []);
    toggleRefreshPlatformDialogOpen(false);
  };

  useEffect(() => {
    if (!isNew) return;
    setValue(
      "otherProductIds",
      storedAnalysisPlatforms?.map((p) => p.id) || [],
    );
    // eslint-disable-next-line
  }, [storedAnalysisPlatforms]);

  return (
    <SHStack>
      <Controller
        name={"otherProductIds"}
        control={control}
        render={({ field: { onChange, ...others } }) => (
          <SelectProductsV2
            {...others}
            multiple
            disabled={disabled}
            badgeText="Current"
            selectedIds={selectedOtherPlatformIds}
            options={platformSelections}
            onChange={async (value) => {
              const result = await getChangeValueResult(
                !Array.isArray(value) ? null : value,
              );
              onChange(result);
            }}
            textFieldProps={{
              label: "Select platforms to include in your analysis",
              placeholder: "Search for platform",
              required: true,
              error: !!errors?.otherProductIds,
              helperText: errors?.otherProductIds?.message,
            }}
          />
        )}
      />

      <ConfirmRefreshAnalysisDataDialog
        open={refreshPlatformDialogOpen}
        onClose={onRefreshDialogClose}
        onSubmit={onRefreshDialogSubmit}
      />
    </SHStack>
  );
};
